import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../useAuth";
import axiosInstance from "@/lib/axios";
import {
  TrackingParameterError,
  TrackingParameterErrorsResult,
  UseTrackingParameterErrorsOptions,
} from "./types";

const trackingParameterErrorKeys = {
  all: ["trackingParameterErrors"] as const,
  lists: () => [...trackingParameterErrorKeys.all, "list"] as const,
  list: (options: Omit<UseTrackingParameterErrorsOptions, "enabled">) =>
    [...trackingParameterErrorKeys.lists(), options] as const,
};

async function fetchTrackingParameterErrors(
  options: Omit<UseTrackingParameterErrorsOptions, "enabled">
): Promise<TrackingParameterErrorsResult> {
  const queryParams = new URLSearchParams();

  queryParams.append("limit", (options.limit ?? 20).toString());
  queryParams.append("offset", (options.offset ?? 0).toString());
  queryParams.append("status", "invalid");

  if (options.sources?.length) {
    options.sources.forEach((source) => queryParams.append("source", source));
  }

  if (options.by_accounts?.length) {
    options.by_accounts.forEach((acc) => {
      queryParams.append("account_id", acc.account_id);
    });
  }

  const response = await axiosInstance.get(
    `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/tracking-parameters/list?${queryParams.toString()}`
  );

  const data = response.data as {
    items: Array<any>;
    count: number;
    count_by_adprovider?: Record<string, number>;
  };
  const totalCount = data.count ?? 0;
  const countByAdProvider = data.count_by_adprovider ?? {};
  const rawItems = Array.isArray(data.items) ? data.items : [];

  const items: TrackingParameterError[] = rawItems.map((item) => ({
    ...item,
    source: item.ad?.source ?? item.source,
    errors: Array.isArray(item.errors)
      ? item.errors
      : item.errors
        ? [item.errors]
        : [],
  }));

  return { items, totalCount, countByAdProvider };
}

export function useTrackingParameterErrors(
  options?: UseTrackingParameterErrorsOptions
) {
  const { data: auth } = useAuth();
  const { enabled = true, ...queryOptions } = options ?? {};

  return useQuery({
    queryKey: trackingParameterErrorKeys.list(queryOptions),
    queryFn: () => fetchTrackingParameterErrors(queryOptions),
    enabled: !!auth?.token && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export { trackingParameterErrorKeys };
